import React, { useEffect, useState } from 'react';
import { adminListStories, adminCreateStory, adminUpdateStory, adminDeleteStory } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Loader2, Plus, Pencil, Trash2, Quote, X } from 'lucide-react';

const empty = { name: '', role: '', tenure: '', quote: '', photo: '', active: true };

export default function AdminStoriesTab() {
  const [stories, setStories] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | story object
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => adminListStories().then(setStories).catch(() => setStories([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setF(empty); setEditing('new'); };
  const openEdit = (s) => { setF({ name: s.name, role: s.role, tenure: s.tenure || '', quote: s.quote, photo: s.photo || '', active: s.active }); setEditing(s); };

  const onPhotoFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const r = new FileReader();
    r.onload = () => setF((prev) => ({ ...prev, photo: r.result }));
    r.readAsDataURL(file);
  };

  const save = async () => {
    if (!f.name || !f.role || f.quote.length < 10) return;
    setBusy(true);
    try {
      if (editing === 'new') await adminCreateStory(f);
      else await adminUpdateStory(editing.id, f);
      setEditing(null);
      await load();
    } finally { setBusy(false); }
  };

  const toggleActive = async (s) => { await adminUpdateStory(s.id, { active: !s.active }); load(); };
  const remove = async (s) => { if (window.confirm(`Delete story by ${s.name}?`)) { await adminDeleteStory(s.id); load(); } };

  return (
    <div className="space-y-4" data-testid="stories-tab">
      <div className="flex items-center justify-between">
        <div className="font-bold flex items-center gap-2"><Quote className="h-4 w-4 text-orange-500" /> Employee Stories {stories ? `(${stories.length})` : ''}</div>
        <Button data-testid="story-add-btn" onClick={openNew} className="h-9 bg-orange-500 hover:bg-orange-600"><Plus className="h-4 w-4 mr-1" /> Add story</Button>
      </div>

      {editing !== null && (
        <div className="rounded-2xl border bg-white p-5 space-y-3" data-testid="story-edit-form">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-sm">{editing === 'new' ? 'New story' : `Editing: ${editing.name}`}</div>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><Label>Name *</Label><Input data-testid="story-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
            <div><Label>Role *</Label><Input data-testid="story-role-input" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} /></div>
            <div><Label>Tenure</Label><Input value={f.tenure} onChange={(e) => setF({ ...f, tenure: e.target.value })} placeholder="e.g. 3 years at Hipro" /></div>
          </div>
          <div><Label>Quote * (min 10 chars)</Label><Textarea data-testid="story-quote-input" rows={3} value={f.quote} onChange={(e) => setF({ ...f, quote: e.target.value })} /></div>
          <div className="grid sm:grid-cols-2 gap-3 items-end">
            <div><Label>Photo URL</Label><Input value={f.photo.startsWith('data:') ? '' : f.photo} onChange={(e) => setF({ ...f, photo: e.target.value })} placeholder="https://… or upload →" /></div>
            <div className="flex items-center gap-3">
              <input type="file" accept="image/*" onChange={onPhotoFile} className="text-xs" />
              {f.photo && <img src={f.photo} alt="preview" className="h-10 w-10 rounded-full object-cover border" />}
            </div>
          </div>
          <Button data-testid="story-save-btn" onClick={save} disabled={busy || !f.name || !f.role || f.quote.length < 10} className="bg-orange-500 hover:bg-orange-600">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save story'}
          </Button>
        </div>
      )}

      {stories === null ? (
        <div className="py-16 text-center text-slate-500"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
      ) : stories.length === 0 ? (
        <div className="py-16 text-center text-slate-500 border border-dashed rounded-2xl">No stories yet — add the first one.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {stories.map((s) => (
            <div key={s.id} data-testid={`admin-story-${s.id}`} className={`rounded-2xl border bg-white p-4 ${s.active ? '' : 'opacity-60'}`}>
              <div className="flex items-start gap-3">
                {s.photo && <img src={s.photo} alt={s.name} className="h-11 w-11 rounded-full object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">{s.name} <span className="font-normal text-slate-500">· {s.role}</span></div>
                  {s.tenure && <div className="text-xs text-slate-500">{s.tenure}</div>}
                  <p className="mt-1.5 text-xs text-slate-600 line-clamp-3">“{s.quote}”</p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button data-testid={`story-toggle-${s.id}`} onClick={() => toggleActive(s)} className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${s.active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500'}`}>
                  {s.active ? 'Live' : 'Hidden'}
                </button>
                <button data-testid={`story-edit-${s.id}`} onClick={() => openEdit(s)} className="text-xs px-2.5 py-1 rounded-full border hover:border-orange-300 inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Edit</button>
                <button data-testid={`story-delete-${s.id}`} onClick={() => remove(s)} className="text-xs px-2.5 py-1 rounded-full border text-red-600 hover:border-red-300 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
